import api from './api';

export interface MSP {
  id: number;
  company: string;
  logoUrl?: string;
  contactName: string;
  phone?: string;
  email: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MSPCustomer {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  isActive: boolean;
  isVerified: boolean;
  lastLogin?: string;
  partnerId?: number;
  inviteCodeUsed?: string;
  createdAt: string;
  updatedAt: string;
  hasActiveCredentials?: boolean;
  monthlySpend?: number;
  projectedSpend?: number;
  percentageChange?: number;
  billingCurrency?: string;
  currency?: string;
}

export interface MSPLoginResponse {
  message: string;
  token: string;
  msp: MSP;
}

export interface MSPCustomersResponse {
  customers: MSPCustomer[];
  stats: {
    directCustomers: number;
    inviteCodeCustomers: number;
    totalCustomers: number;
    activeOpportunities: number;
    totalMonthlySpend?: number;
    leadsCount?: number;
  };
  total: number;
  page: number;
  totalPages: number;
}

export interface ImpersonateResponse {
  success: boolean;
  token: string;
  customer: {
    id: number;
    email: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    isActive: boolean;
    isVerified: boolean;
  };
  impersonatedBy: {
    partnerId: number;
    partnerCompany: string;
  };
}

class MSPService {
  private readonly MSP_TOKEN_KEY = 'mspToken';
  private readonly MSP_DATA_KEY = 'mspData';
  private readonly IMPERSONATION_DATA_KEY = 'impersonationData';

  async login(email: string, password: string): Promise<MSPLoginResponse> {
    const response = await api.post<MSPLoginResponse>('/msp/login', {
      email,
      password
    });

    if (response.data.token) {
      // Store partner token and data
      localStorage.setItem(this.MSP_TOKEN_KEY, response.data.token);
      localStorage.setItem(this.MSP_DATA_KEY, JSON.stringify(response.data.msp));
      
      // Set authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }

    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await api.post('/msp/logout');
    } catch (error) {
      console.error('Partner logout error:', error);
    } finally {
      // Clear partner data
      localStorage.removeItem(this.MSP_TOKEN_KEY);
      localStorage.removeItem(this.MSP_DATA_KEY);
      localStorage.removeItem(this.IMPERSONATION_DATA_KEY);
      
      // Clear auth header
      delete api.defaults.headers.common['Authorization'];
    }
  }

  async getProfile(): Promise<MSP> {
    const response = await api.get<{ msp: MSP }>('/msp/profile');
    return response.data.msp;
  }

  async updateProfile(formData: FormData): Promise<MSP> {
    const response = await api.put<{ partner: MSP }>('/msp/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.partner;
  }

  async getCustomers(params?: {
    search?: string;
    page?: number;
    limit?: number;
    hasActiveCredentials?: boolean;
    isActive?: boolean;
  }): Promise<MSPCustomersResponse> {
    const response = await api.get<MSPCustomersResponse>('/msp/customers', { params });
    return response.data;
  }

  async impersonateCustomer(customerId: number): Promise<ImpersonateResponse> {
    const response = await api.post<ImpersonateResponse>(`/msp/customers/${customerId}/impersonate`);
    
    if (response.data.success && response.data.token) {
      // Store impersonation data
      localStorage.setItem(this.IMPERSONATION_DATA_KEY, JSON.stringify({
        token: response.data.token,
        customer: response.data.customer,
        impersonatedBy: response.data.impersonatedBy
      }));
      
      // Switch to the customer token
      localStorage.setItem('authToken', response.data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }

    return response.data;
  }

  async createQuickCustomer(data: {
    firstName: string;
    lastName: string;
    companyName: string;
    email: string;
    inviteCode: string;
  }): Promise<any> {
    const response = await api.post('/msp/customers/quick-create', data);
    return response.data;
  }

  async updateCustomer(customerId: number, data: { isActive?: boolean }): Promise<any> {
    const response = await api.patch(`/msp/customers/${customerId}`, data);
    return response.data;
  }

  endImpersonation(): void {
    // Restore partner token
    const partnerToken = localStorage.getItem(this.MSP_TOKEN_KEY);
    if (partnerToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${partnerToken}`;
    }
    
    // Clear impersonation data
    localStorage.removeItem(this.IMPERSONATION_DATA_KEY);
    localStorage.removeItem('authToken');
  }

  isMSPAuthenticated(): boolean {
    return !!localStorage.getItem(this.MSP_TOKEN_KEY);
  }

  isImpersonating(): boolean {
    return !!localStorage.getItem(this.IMPERSONATION_DATA_KEY);
  }

  getMSPData(): MSP | null {
    const data = localStorage.getItem(this.MSP_DATA_KEY);
    if (!data || data === 'undefined' || data === 'null') {
      return null;
    }
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing MSP data:', error);
      localStorage.removeItem(this.MSP_DATA_KEY);
      return null;
    }
  }

  getImpersonationData(): {
    token: string;
    customer: MSPCustomer;
    impersonatedBy: { mspId: number; mspCompany?: string; partnerCompany?: string };
  } | null {
    const data = localStorage.getItem(this.IMPERSONATION_DATA_KEY);
    if (!data || data === 'undefined' || data === 'null') {
      return null;
    }
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing impersonation data:', error);
      localStorage.removeItem(this.IMPERSONATION_DATA_KEY);
      return null;
    }
  }

  // Initialize partner auth from stored token
  initializeAuth(): void {
    const token = localStorage.getItem(this.MSP_TOKEN_KEY);
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }
}

export default new MSPService();